import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const posts = [
  {
    id: 1,
    category: 'Water Damage',
    title: 'What to Do in the First 24 Hours After Water Damage',
    excerpt: 'Acting fast after water damage is critical. Learn the immediate steps you should take to minimize structural damage, prevent mold growth, and protect your belongings.',
    date: 'June 10, 2025',
    readTime: '5 min read',
    image: 'https://public.readdy.ai/ai/img_res/5784cb851d7d12c94b7a4a0e35d21fad.jpg',
  },
  {
    id: 2,
    category: 'Mold Prevention',
    title: 'How to Prevent Mold After a Flood: A Complete Guide',
    excerpt: 'Mold can begin growing within 24–48 hours of water exposure. Discover the proven strategies our Merced restoration experts use to stop mold before it starts.',
    date: 'May 28, 2025',
    readTime: '7 min read',
    image: 'https://readdy.ai/api/search-image?query=professional%20mold%20remediation%20specialist%20in%20protective%20gear%20inspecting%20wall%20with%20moisture%20meter%20in%20residential%20home%20clean%20bright%20interior&width=400&height=250&seq=blog2-indoor&orientation=landscape',
  },
  {
    id: 3,
    category: 'Insurance Tips',
    title: 'Navigating Your Homeowner\'s Insurance Claim for Water Damage',
    excerpt: 'Filing a water damage insurance claim can be overwhelming. We break down the process step by step so you get the maximum payout you deserve.',
    date: 'May 14, 2025',
    readTime: '6 min read',
    image: 'https://readdy.ai/api/search-image?query=insurance%20agent%20and%20homeowner%20reviewing%20documents%20at%20kitchen%20table%20after%20water%20damage%20claim%20paperwork%20professional%20setting%20warm%20lighting&width=400&height=250&seq=blog3-indoor&orientation=landscape',
  },
  {
    id: 4,
    category: 'Restoration Tips',
    title: 'Structural Drying 101: Why Industrial Equipment Matters',
    excerpt: 'Consumer fans and dehumidifiers are no match for professional-grade drying equipment. Learn why proper structural drying is essential to a full restoration.',
    date: 'April 30, 2025',
    readTime: '4 min read',
    image: 'https://readdy.ai/api/search-image?query=industrial%20air%20movers%20and%20dehumidifiers%20set%20up%20in%20empty%20room%20during%20water%20damage%20restoration%20process%20professional%20equipment%20clean%20space&width=400&height=250&seq=blog4-indoor&orientation=landscape',
  },
  {
    id: 5,
    category: 'Local News',
    title: 'Merced County Flood Season: What Homeowners Need to Know',
    excerpt: 'With Central Valley rainy seasons becoming more intense, Merced homeowners should be prepared. Here\'s our local expert guide to flood readiness.',
    date: 'April 15, 2025',
    readTime: '5 min read',
    image: 'https://readdy.ai/api/search-image?query=aerial%20view%20of%20Central%20Valley%20California%20farmland%20with%20flooded%20fields%20during%20rainy%20season%20overcast%20sky%20dramatic%20landscape%20photography&width=400&height=250&seq=blog5-indoor&orientation=landscape',
  },
  {
    id: 6,
    category: 'Fire & Smoke',
    title: 'Fire Damage Restoration: From Smoke Odor to Full Recovery',
    excerpt: 'Fire damage goes beyond what you can see. Smoke and soot penetrate walls, HVAC systems, and furniture. Learn how our team restores homes to pre-loss condition.',
    date: 'March 22, 2025',
    readTime: '6 min read',
    image: 'https://readdy.ai/api/search-image?query=restoration%20technician%20cleaning%20smoke%20damaged%20wall%20in%20residential%20home%20after%20fire%20professional%20equipment%20protective%20gear%20interior%20photography&width=400&height=250&seq=blog6-indoor&orientation=landscape',
  },
];

const categories = ['All', 'Water Damage', 'Mold Prevention', 'Insurance Tips', 'Restoration Tips', 'Local News', 'Fire & Smoke'];

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    window.scrollTo(0, 0);

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Indoor-Restore Services Blog",
      "description": "Expert tips, guides, and local news about water damage restoration, mold prevention, fire damage cleanup, and emergency restoration services in Merced, CA.",
      "url": `${import.meta.env.VITE_SITE_URL}/blog`,
      "publisher": {
        "@type": "Organization",
        "name": "Indoor-Restore Services",
        "url": import.meta.env.VITE_SITE_URL,
        "logo": {
          "@type": "ImageObject",
          "url": "https://mercedwaterrestoration.com/wp-content/uploads/2024/11/Indoor-Restore-Services-Logo.png"
        }
      },
      "blogPost": posts.map(post => ({
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.excerpt,
        "datePublished": new Date(post.date).toISOString(),
        "author": {
          "@type": "Organization",
          "name": "Indoor-Restore Services"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Indoor-Restore Services"
        },
        "image": post.image
      }))
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const filtered = activeCategory === 'All' ? posts : posts.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-teal-700 to-teal-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20water%20texture%20ripple%20pattern%20teal%20blue%20professional%20background%20minimal%20clean%20design&width=1280&height=300&seq=blog-hero-indoor&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block bg-teal-500/30 text-teal-100 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-widest">
            Expert Insights
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Restoration Blog</h1>
          <p className="text-teal-100 text-lg max-w-2xl mx-auto">
            Tips, guides, and local news from the Indoor-Restore Services team in Merced, CA.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-20 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 overflow-x-auto py-4 scrollbar-hide">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-teal-600 text-white shadow'
                    : 'bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-700'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map(post => {
              // First article links to detail page
              if (post.id === 1) {
                return (
                  <Link
                    key={post.id}
                    to="/blog/water-damage-first-24-hours"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer block"
                  >
                    <div className="w-full h-52 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                        <span className="text-gray-400 text-xs">{post.readTime}</span>
                      </div>
                      <h2 className="text-base font-bold text-gray-900 mb-2 leading-snug group-hover:text-teal-700 transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-xs">{post.date}</span>
                        <span className="text-teal-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                          Read More <i className="ri-arrow-right-line"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              }

              // Second article (Mold Prevention) links to detail page
              if (post.id === 2) {
                return (
                  <Link
                    key={post.id}
                    to="/blog/prevent-mold-after-flood"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer block"
                  >
                    <div className="w-full h-52 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                        <span className="text-gray-400 text-xs">{post.readTime}</span>
                      </div>
                      <h2 className="text-base font-bold text-gray-900 mb-2 leading-snug group-hover:text-teal-700 transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-xs">{post.date}</span>
                        <span className="text-teal-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                          Read More <i className="ri-arrow-right-line"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              }

              // Third article (Insurance Tips) links to detail page
              if (post.id === 3) {
                return (
                  <Link
                    key={post.id}
                    to="/blog/homeowners-insurance-claim-water-damage"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer block"
                  >
                    <div className="w-full h-52 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                        <span className="text-gray-400 text-xs">{post.readTime}</span>
                      </div>
                      <h2 className="text-base font-bold text-gray-900 mb-2 leading-snug group-hover:text-teal-700 transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-xs">{post.date}</span>
                        <span className="text-teal-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                          Read More <i className="ri-arrow-right-line"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              }

              // Fourth article (Structural Drying) links to detail page
              if (post.id === 4) {
                return (
                  <Link
                    key={post.id}
                    to="/blog/structural-drying-101"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer block"
                  >
                    <div className="w-full h-52 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                        <span className="text-gray-400 text-xs">{post.readTime}</span>
                      </div>
                      <h2 className="text-base font-bold text-gray-900 mb-2 leading-snug group-hover:text-teal-700 transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-xs">{post.date}</span>
                        <span className="text-teal-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                          Read More <i className="ri-arrow-right-line"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              }

              // Other articles remain as non-linked cards
              return post.id === 5 ? (
                <Link
                  key={post.id}
                  to="/blog/merced-county-flood-season"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                  className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer block"
                >
                  <div className="w-full h-52 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">
                        {post.category}
                      </span>
                      <span className="text-gray-400 text-xs">{post.readTime}</span>
                    </div>
                    <h2 className="text-base font-bold text-gray-900 mb-2 leading-snug group-hover:text-teal-700 transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs">{post.date}</span>
                      <span className="text-teal-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read More <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ) : (
                <Link
                  key={post.id}
                  to="/blog/fire-damage-restoration-smoke-odor-recovery"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                  className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer block"
                >
                  <div className="w-full h-52 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">
                        {post.category}
                      </span>
                      <span className="text-gray-400 text-xs">{post.readTime}</span>
                    </div>
                    <h2 className="text-base font-bold text-gray-900 mb-2 leading-snug group-hover:text-teal-700 transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs">{post.date}</span>
                      <span className="text-teal-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read More <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 text-gray-400">
              <i className="ri-article-line text-5xl mb-4 block"></i>
              <p className="text-lg">No posts in this category yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-teal-700 py-14">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">Dealing with Water Damage Right Now?</h3>
          <p className="text-teal-100 mb-6 text-sm">Don't wait — call our 24/7 emergency line for immediate help in Merced, CA.</p>
          <a
            href="tel:2094512288"
            className="inline-flex items-center gap-2 bg-white text-teal-700 px-8 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line"></i>
            (209) 451-2288
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
